import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, Typography, IconButton, Paper, Divider, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import statsService from '../services/StatisticsService';

const StatsSection = ({ title, children }) => (
    <Box mb={3}>
        <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
            {title}
        </Typography>
        <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <Grid container spacing={2}>
                {children}
            </Grid>
        </Paper>
    </Box>
);

const StatItem = ({ label, value }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Box display="flex" flexDirection="column">
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {label}
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                {value}
            </Typography>
        </Box>
    </Grid>
);

const StatisticsModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (open) {
            setStats(statsService.loadStats());
        }
    }, [open]);

    if (!stats) return null;

    const s = stats;
    const a = stats.actions;
    const r = stats.records;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 20,
                    background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="span" fontWeight="bold">
                    {t('statistics.title')}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
            <DialogContent>
                <StatsSection title={t('statistics.general')}>
                    <StatItem label={t('statistics.games_played')} value={s.gamesPlayed} />
                    <StatItem label={t('statistics.ronda_games')} value={s.rondaGames} />
                    <StatItem label={t('statistics.baciga_games')} value={s.bacigaGames} />
                    <StatItem label={t('statistics.total_wins')} value={s.wins.total} />
                    <StatItem label={t('statistics.win_rate')} value={`${s.percentages.winRate}%`} />
                    <StatItem label={t('statistics.ronda_win_rate')} value={`${s.percentages.rondaWinRate}%`} />
                    <StatItem label={t('statistics.baciga_win_rate')} value={`${s.percentages.bacigaWinRate}%`} />
                </StatsSection>

                <StatsSection title={t('statistics.player_actions')}>
                    <StatItem label={t('statistics.caidas')} value={a.player.caidas} />
                    <StatItem label={t('statistics.contracaidas')} value={a.player.contracaidas} />
                    <StatItem label={t('statistics.san_vicentes')} value={a.player.sanVicentes} />
                    <StatItem label={t('statistics.aseos')} value={a.player.aseos} />
                    <StatItem label={t('statistics.rondas')} value={a.player.rondas} />
                    <StatItem label={t('statistics.rondines')} value={a.player.rondines} />
                    <StatItem label={t('statistics.bacigas')} value={a.player.bacigas} />
                    <StatItem label={t('statistics.bacigotes')} value={a.player.bacigotes} />
                    <StatItem label={t('statistics.cosas')} value={a.player.cosas} />
                    <StatItem label={t('statistics.las31')} value={a.player.las31} />
                </StatsSection>

                <StatsSection title={t('statistics.partner_results')}>
                    <StatItem label={t('statistics.caidas')} value={a.partner.caidas} />
                    <StatItem label={t('statistics.contracaidas')} value={a.partner.contracaidas} />
                    <StatItem label={t('statistics.san_vicentes')} value={a.partner.sanVicentes} />
                    <StatItem label={t('statistics.aseos')} value={a.partner.aseos} />
                    <StatItem label={t('statistics.rondas')} value={a.partner.rondas} />
                    <StatItem label={t('statistics.rondines')} value={a.partner.rondines} />
                    <StatItem label={t('statistics.bacigas')} value={a.partner.bacigas} />
                    <StatItem label={t('statistics.bacigotes')} value={a.partner.bacigotes} />
                    <StatItem label={t('statistics.cosas')} value={a.partner.cosas} />
                    <StatItem label={t('statistics.las31')} value={a.partner.las31} />
                </StatsSection>
                <StatsSection title={t('statistics.rivals_results')}>
                    <StatItem label={t('statistics.caidas')} value={a.rivals.caidas} />
                    <StatItem label={t('statistics.contracaidas')} value={a.rivals.contracaidas} />
                    <StatItem label={t('statistics.san_vicentes')} value={a.rivals.sanVicentes} />
                    <StatItem label={t('statistics.aseos')} value={a.rivals.aseos} />
                    <StatItem label={t('statistics.rondas')} value={a.rivals.rondas} />
                    <StatItem label={t('statistics.rondines')} value={a.rivals.rondines} />
                    <StatItem label={t('statistics.bacigas')} value={a.rivals.bacigas} />
                    <StatItem label={t('statistics.bacigotes')} value={a.rivals.bacigotes} />
                    <StatItem label={t('statistics.cosas')} value={a.rivals.cosas} />
                    <StatItem label={t('statistics.las31')} value={a.rivals.las31} />
                </StatsSection>

                <StatsSection title={t('statistics.records')}>
                    <StatItem label={t('statistics.max_cards_hand_team')} value={r.maxCardsHandTeam} />
                    <StatItem label={t('statistics.max_cards_hand_player')} value={r.maxCardsHandPlayer} />
                    <StatItem label={t('statistics.lowest_baciga_player')} value={r.lowestBacigaPlayer === 99 ? '-' : r.lowestBacigaPlayer} />
                    <StatItem label={t('statistics.lowest_bacigote_player')} value={r.lowestBacigotePlayer === 99 ? '-' : r.lowestBacigotePlayer} />
                </StatsSection>

                <StatsSection title={`🎉 ${t('statistics.miscellaneous')}`}>
                    <StatItem label={t('statistics.total_points_scored')} value={s.funStats?.totalPointsScored || 0} />
                    <StatItem label={t('statistics.total_cards_collected')} value={s.funStats?.totalCardsCollected || 0} />
                    <StatItem
                        label={t('statistics.avg_points_per_game')}
                        value={s.gamesPlayed > 0 ? ((s.funStats?.totalPointsScored || 0) / s.gamesPlayed).toFixed(1) : '0'}
                    />
                    <StatItem
                        label={t('statistics.avg_cards_per_game')}
                        value={s.rondaGames > 0 ? ((s.funStats?.totalCardsCollected || 0) / s.rondaGames).toFixed(1) : '0'}
                    />
                    <StatItem
                        label={t('statistics.caidas_aseos_ratio')}
                        value={
                            s.actions.player.aseos > 0
                                ? ((s.actions.player.caidas + s.actions.player.contracaidas + s.actions.player.sanVicentes) / s.actions.player.aseos).toFixed(2)
                                : '-'
                        }
                    />
                    <StatItem
                        label={t('statistics.total_specials')}
                        value={
                            (s.actions.player.caidas || 0) +
                            (s.actions.player.contracaidas || 0) +
                            (s.actions.player.sanVicentes || 0) +
                            (s.actions.player.aseos || 0) +
                            (s.actions.player.rondas || 0) +
                            (s.actions.player.rondines || 0) +
                            (s.actions.player.bacigas || 0) +
                            (s.actions.player.bacigotes || 0) +
                            (s.actions.player.cosas || 0)
                        }
                    />
                </StatsSection>
            </DialogContent>
        </Dialog>
    );
};

export default StatisticsModal;
